import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Geohublogoo from './Images/Geohublogoo.png';
import Geohublogo from './Images/Geohublogoo.png';
import API_CONFIG from './config';
import ReactMarkdown from 'react-markdown';
import { updateConversationTitle, debugConversations } from './utils/apiServices';

// Add mobile detection hook
function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);

  return isMobile;
}

const Sidebar = ({ onNewChat, handleOpenUserSettings, isSidebarOpen, handleToggleSidebar, onSelectConversation, currentConversation, currentConversationId, externalConversations }) => {
  const [conversations, setConversations] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [username, setUsername] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [selectedQuickFilter, setSelectedQuickFilter] = useState('');
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState(null);
  const [openUpwards, setOpenUpwards] = useState(false);
  const menuRefs = useRef({});
  const sidebarRef = useRef(null);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  
  // Add useParams and useLocation hooks to get URL parameters
  const { conversationId: urlConversationId } = useParams();
  const location = useLocation();

  // Add state for inline title editing
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [hasLocalUpdates, setHasLocalUpdates] = useState(false);
  const [forceRenderKey, setForceRenderKey] = useState(0);
  const [updatedConversationId, setUpdatedConversationId] = useState(null);
  const [updatedTitles, setUpdatedTitles] = useState({});
  const [hasCalledHistoryAfterResponse, setHasCalledHistoryAfterResponse] = useState(false);
  const hasCalledHistoryAfterResponseRef = useRef(false);

  // Debug effect to monitor conversations state changes
  useEffect(() => {

    
    // Check if any conversation has been updated
    conversations.forEach(convo => {
      if (convo.title && convo.title !== 'New Conversation') {

      }
    });
  }, [conversations]);

  // Additional effect to monitor specific conversation updates
  useEffect(() => {
    if (conversations.length > 0) {
      const testConversation = conversations.find(c => c.title === 'test1');
      if (testConversation) {

      }
    }
  }, [conversations]);

  // Helper function to sort conversations consistently
  const sortConversations = (convos) => {

    return convos.sort((a, b) => {
      // If both are pinned or both are unpinned, sort by timestamp
      if (a.is_pinned === b.is_pinned) {
        // For pinned chats, use pin_timestamp first if available
        if (a.is_pinned) {
          const aTime = a.pin_timestamp || a.updated_at;
          const bTime = b.pin_timestamp || b.updated_at;
          return new Date(bTime) - new Date(aTime); // Newest first
        }
        // For unpinned, sort by updated_at
        return new Date(b.updated_at) - new Date(a.updated_at);
      }
      // Otherwise, pinned chats go first
      return a.is_pinned ? -1 : 1;
    });
  };

  // Helper function to filter conversations based on search query and date range
  const filterConversations = (convos, query, fromDate, toDate) => {
    let filteredConvos = convos;
    
    // Filter by keyword search
    if (query.trim()) {
      const searchTerm = query.toLowerCase().trim();
      filteredConvos = filteredConvos.filter(convo => {
        // Get the full title for searching
        const fullTitle = convo.conversations?.title ||
          convo.title ||
          (convo.chat_history && convo.chat_history[0]?.content) ||
          'New Conversation';
        
        // Search in title
        if (fullTitle.toLowerCase().includes(searchTerm)) {
          return true;
        }
        
        // Search in chat history content if available
        if (convo.chat_history && Array.isArray(convo.chat_history)) {
          return convo.chat_history.some(message => 
            message.content && message.content.toLowerCase().includes(searchTerm)
          );
        }
        
        return false;
      });
    }
    
    // Filter by date range
    if (fromDate || toDate) {
      filteredConvos = filteredConvos.filter(convo => {
        const conversationDate = new Date(convo.updated_at);
        
        if (fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999); // Include the entire end date
          return conversationDate >= from && conversationDate <= to;
        } else if (fromDate) {
          const from = new Date(fromDate);
          return conversationDate >= from;
        } else if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999); // Include the entire end date
          return conversationDate <= to;
        }
        
        return true;
      });
    }
    
    return filteredConvos;
  };

  // Helper function to handle quick filter selections
  const handleQuickFilter = (filterType) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let fromDate = '';
    let toDate = '';
    
    switch (filterType) {
      case 'today':
        fromDate = today.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'yesterday':
        fromDate = yesterday.toISOString().split('T')[0];
        toDate = yesterday.toISOString().split('T')[0];
        break;
      case 'past7days':
        const past7Days = new Date(today);
        past7Days.setDate(past7Days.getDate() - 7);
        fromDate = past7Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'past30days':
        const past30Days = new Date(today);
        past30Days.setDate(past30Days.getDate() - 30);
        fromDate = past30Days.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'thismonth':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDate = firstDayOfMonth.toISOString().split('T')[0];
        toDate = today.toISOString().split('T')[0];
        break;
      case 'custom':
        // Keep existing custom dates
        break;
      case 'clear':
        fromDate = '';
        toDate = '';
        break;
      default:
        break;
    }
    
    setDateFrom(fromDate);
    setDateTo(toDate);
    setSelectedQuickFilter(filterType);
  };

  // Check authentication status on component mount
  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const userId = localStorage.getItem('userId');
    const storedUsername = localStorage.getItem('userName');
    setIsAuthenticated(!!authToken && !!userId);
    setUsername(storedUsername || '');
  }, []);

  // Sync selectedConversationId with URL parameter
  useEffect(() => {
    if (urlConversationId) {
      setSelectedConversationId(urlConversationId);
    } else if (location.pathname === '/') {
      // Clear selection when on root path
      setSelectedConversationId(null);
    }
  }, [urlConversationId, location.pathname]);

  // Sync selectedConversationId with currentConversationId from parent (fallback)
  useEffect(() => {
    if (currentConversationId && !urlConversationId) {
      setSelectedConversationId(currentConversationId);
    }
  }, [currentConversationId, urlConversationId]);

  // Helper function to check if conversation IDs match
  const isConversationSelected = (conversation) => {
    if (!selectedConversationId) return false;
    
    const conversationId = conversation.conversation_id || conversation.id;
    
    // Convert both to strings for comparison to handle different types
    return String(selectedConversationId) === String(conversationId);
  };

  // Ensure conversations are always properly filtered and sorted
  useEffect(() => {
    if (conversations.length > 0) {
      // Filter out archived conversations and apply consistent sorting
      setConversations(prevConversations => {
        const filteredConvos = prevConversations.filter(convo => !convo.is_archived);
        return sortConversations(filteredConvos);
      });
    }
  }, []); // Run once on mount

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (openMenuId && menuRefs.current[openMenuId] &&
        !menuRefs.current[openMenuId].contains(event.target)) {
        setOpenMenuId(null);
        setOpenUpwards(false);
      }
      
      // Cancel editing if clicking outside the editing area
      if (editingConversationId && !event.target.closest('.chat-title')) {
        handleCancelRename();
      }
    }

    // Use capture phase to ensure we catch all clicks
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [openMenuId, editingConversationId]);

  // Handle keyboard navigation for dropdown
  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "Escape" && openMenuId) {
        setOpenMenuId(null);
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [openMenuId]);

  // Define fetchHistory function outside of useEffect to make it reusable
  const fetchHistory = useCallback(async () => {
    try {
      // console.log('🔄 FETCHING HISTORY...');
      const userId = localStorage.getItem('userId');
      
      if (!userId) {
        // console.log('⚠️ Missing user ID, skipping history fetch');
        return;
      }
      
      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.HISTORY}`;
      // console.log('📡 Fetching from URL:', url);
      
      // Import the API client dynamically to avoid circular dependency
      const { apiClient } = await import('./utils/apiClient');
      
      const response = await apiClient.post(url, {
        user_id: userId
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }
      
      const data = await response.json();
      // console.log('📦 Received history data:', data);
      
      const updatedConversations = Array.isArray(data.conversations) 
        ? data.conversations 
        : Array.isArray(data) 
          ? data 
          : [];

      // console.log('🔄 Processing conversations:', updatedConversations.length);

      updatedConversations.forEach((convo, index) => {
        // console.log(`📝 Conversation ${index}:`, convo.id || convo.conversation_id, convo.title || convo.conversations?.title);
      });

        // Update conversations with new data
        setConversations(prevConversations => {
        // If data is empty, keep existing conversations
        if (!updatedConversations || updatedConversations.length === 0) {
          // console.log('⚠️ No conversations in response, keeping existing conversations');
          return prevConversations;
        }

        // Filter out archived conversations
        const filteredConversations = updatedConversations.filter(convo => !convo.is_archived);

        const newConversations = filteredConversations.map(convo => {
            // Get the title from the correct location in the API response
            let title = convo.conversations?.title || 
                       convo.title || 
                       (convo.chat_history && convo.chat_history[0]?.content) || 
                       'New Conversation';
            
            // If the title is too long, truncate it
            if (title && title.length > 30) {
              title = title.substring(0, 30) + '...';
            }

          // Handle different possible locations for is_pinned and is_archived
          const isPinned = convo.is_pinned ||
            convo.conversations?.is_pinned ||
            convo.pinned ||
            false;

          const isArchived = convo.is_archived ||
            convo.conversations?.is_archived ||
            convo.archived ||
            false;

            return {
              ...convo,
              title: title,
              id: convo.id || convo.conversation_id,
              conversation_id: convo.conversation_id || convo.id,
            updated_at: convo.updated_at || new Date().toISOString(),
            is_pinned: !!isPinned, // Ensure is_pinned is a boolean
            is_archived: !!isArchived // Ensure is_archived is a boolean
            };
          });

        const sortedConversations = sortConversations(newConversations);
        // console.log('✅ Updated conversations state with:', sortedConversations.length, 'conversations');
        return sortedConversations;
        });
      } catch (error) {
        console.error('❌ Error fetching history:', error);
    }
  }, []);

  // Load conversations on component mount
  useEffect(() => {
    const loadInitialConversations = async () => {
      // console.log('Loading initial conversations...');
      await fetchHistory();
    };

    if (isAuthenticated) {
      loadInitialConversations();
    }
  }, [isAuthenticated, fetchHistory]);

  // Add effect to handle conversations deleted event
  useEffect(() => {
    const handleConversationDeleted = (event) => {

      if (!event.detail || !event.detail.conversationId) {

          return;
      }

        fetchHistory();
    };

    // console.log('Setting up conversationDeleted event listener');
    window.addEventListener('conversationDeleted', handleConversationDeleted);
    
    return () => {
      // console.log('Cleaning up conversationDeleted event listener');
      window.removeEventListener('conversationDeleted', handleConversationDeleted);
    };
  }, [fetchHistory]);

  useEffect(() => {
    const handleConversationUnarchived = (event) => {

      if (!event.detail || !event.detail.conversationId) {
          console.error('ERROR: No conversation ID in event detail, skipping history fetch');

          return;
      }

      fetchHistory();
    };

    window.addEventListener('conversationUnarchived', handleConversationUnarchived);
    
    return () => {
      window.removeEventListener('conversationUnarchived', handleConversationUnarchived);
    };
  }, [fetchHistory]);

  // Add effect to handle chat response completion
  useEffect(() => {
    const handleChatResponseComplete = (event) => {
      // console.log('🔄 CHAT RESPONSE COMPLETE EVENT RECEIVED:', event.detail);
   
      if (!event.detail || !event.detail.conversationId) {
        console.error('ERROR: No conversation ID in event detail, skipping history fetch');
        return;
      }

      // Only fetch history after the first AI response (responseCount === 1)
      if (event.detail.responseCount === 1) {
        // console.log('✅ Fetching updated title after first AI response for conversation:', event.detail.conversationId);
        
        // Check if we've already called history for this response
        if (hasCalledHistoryAfterResponseRef.current) {
          // console.log('⚠️ History already called for this response, skipping');
          return;
        }

        // console.log('🔄 Calling fetchHistory() to update sidebar...');
        
        // Add a delay to ensure the backend has processed the new conversation
        setTimeout(() => {
          // console.log('⏰ Delayed fetchHistory() call after 1 second');
          fetchHistory();
          setHasCalledHistoryAfterResponse(true);
          hasCalledHistoryAfterResponseRef.current = true;
        }, 2000);
      }
    };

    // console.log('🎧 Setting up chatResponseComplete event listener');
    window.addEventListener('chatResponseComplete', handleChatResponseComplete);
    
    return () => {
      // console.log('🧹 Cleaning up chatResponseComplete event listener');
      window.removeEventListener('chatResponseComplete', handleChatResponseComplete);
    };
  }, []); // Remove dependencies to prevent event listener cleanup/setup cycles

  // Update conversations when externalConversations changes
  useEffect(() => {
    // Don't override if we have local updates
    if (hasLocalUpdates) {
      
      return;
    }
    
    if (externalConversations && externalConversations.length > 0) {
      // Filter out archived conversations
      const filteredConversations = externalConversations.filter(convo => !convo.is_archived);

      // Format the conversations to ensure proper title handling
      const formattedConversations = filteredConversations.map(convo => {
        // Get the title from the correct location in the API response
        let title = convo.conversations?.title || 
                   convo.title || 
                   (convo.chat_history && convo.chat_history[0]?.content) || 
                   'New Conversation';
        
        // If the title is too long, truncate it
        if (title && title.length > 30) {
          title = title.substring(0, 30) + '...';
        }

        // Handle different possible locations for is_pinned and is_archived
        const isPinned = convo.is_pinned ||
          convo.conversations?.is_pinned ||
          convo.pinned ||
          false;

        const isArchived = convo.is_archived ||
          convo.conversations?.is_archived ||
          convo.archived ||
          false;

        return {
          ...convo,
          title: title,
          id: convo.id || convo.conversation_id,
          conversation_id: convo.conversation_id || convo.id,
          updated_at: convo.updated_at || new Date().toISOString(),
          is_pinned: !!isPinned, // Ensure is_pinned is a boolean
          is_archived: !!isArchived // Ensure is_archived is a boolean
        };
      });

      // Merge with existing conversations, preserving local changes
      setConversations(prevConversations => {
        const merged = formattedConversations.map(newConvo => {
          const existingConvo = prevConversations.find(c => 
            (c.id === newConvo.id || c.conversation_id === newConvo.conversation_id) ||
            (c.id === newConvo.conversation_id || c.conversation_id === newConvo.id)
          );
          
          // If we have an existing conversation with a different title, preserve the existing one
          // This prevents external updates from overriding local title changes
          if (existingConvo && existingConvo.title !== newConvo.title) {
    
            return { ...newConvo, title: existingConvo.title };
          }
          
          return newConvo;
        });
        
        return sortConversations(merged);
      });
    }
  }, [externalConversations, hasLocalUpdates]);

  // Effect to handle conversation updates
  useEffect(() => {
    if (!currentConversationId || !currentConversation || currentConversation.length === 0) {
      return;
    }

    setConversations(prevConversations => {
      const conversationIndex = prevConversations.findIndex(
        convo => (convo.id === currentConversationId || convo.conversation_id === currentConversationId)
      );

      if (conversationIndex >= 0) {
        const updatedConversations = [...prevConversations];
        const latestMessage = currentConversation[currentConversation.length - 1];
        const existingConversation = updatedConversations[conversationIndex];
        
        // Only update title if it hasn't been manually edited and it's one of the first few user messages
        const shouldUpdateTitle = 
          latestMessage && 
          latestMessage.role === 'user' && 
          currentConversation.filter(msg => msg.role === 'user').length <= 2 &&
          // Don't update if the title has been manually set (not the default "New Conversation")
          existingConversation.title !== 'New Conversation' &&
          // Don't update if we have local updates
          !hasLocalUpdates;
        
        // Update the conversation with the latest data
        updatedConversations[conversationIndex] = {
          ...updatedConversations[conversationIndex],
          // Update title if it's a user message and it's one of the first few messages
          ...(shouldUpdateTitle && {
            title: latestMessage.content.length > 30 
              ? latestMessage.content.substring(0, 30) + '...' 
              : latestMessage.content
          }),
          updated_at: new Date().toISOString()
        };
        
        // Use the helper function to sort conversations
        return sortConversations(updatedConversations);
      }
      return prevConversations;
    });
  }, [currentConversationId, currentConversation, hasLocalUpdates]);

  // Effect to handle conversation created events
  useEffect(() => {
    const handleConversationCreated = (event) => {
      console.log('🎉 NEWWWWW CONVERSATION CREATED EVENT RECEIVED :', event.detail);
      const conversation = event.detail; // The conversation data is directly in event.detail
      if (conversation && conversation.id) {
        console.log('✅ Adding new conversation to sidebar:', conversation.id, conversation.title);
        
        // Reset the history flag for new conversations
        setHasCalledHistoryAfterResponse(false);
        hasCalledHistoryAfterResponseRef.current = false;
        
        setConversations(prevConversations => {
          // Check if conversation already exists to prevent duplicates
          const existingConversation = prevConversations.find(conv => 
            conv.id === conversation.id || conv.conversation_id === conversation.id
          );
          
          if (existingConversation) {
            console.log('⚠️ Conversation already exists in sidebar, skipping duplicate');
            return prevConversations;
          }
          
          const newConversation = {
            id: conversation.id,
            conversation_id: conversation.id,
            title: conversation.title || 'New Conversation',
            is_pinned: false,
            is_archived: false,
            is_liked: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          // Add the new conversation to the beginning of the list
          const updatedConversations = [newConversation, ...prevConversations];
          console.log('📝 Updated conversations list with new conversation');
          return sortConversations(updatedConversations);
        });
        
        // Update the current conversation ID
        if (onSelectConversation) {
          onSelectConversation(conversation);
        }
      } else {
        console.log('⚠️ Invalid conversation data in event:', conversation);
      }
    };

    // Handle conversation forking (when shared conversation is forked)
    const handleConversationForked = (event) => {
      // console.log('🔄 CONVERSATION FORKED EVENT RECEIVED:', event.detail);
      const { oldConversationId, newConversationId, title } = event.detail;
      
      setConversations(prevConversations => {
        // Remove the old conversation (if it exists in the list)
        const filteredConversations = prevConversations.filter(conv => conv.id !== oldConversationId);
        
        // Add the new forked conversation
        const newConversation = {
          id: newConversationId,
          conversation_id: newConversationId,
          title: title || 'Forked Conversation',
          is_pinned: false,
          is_archived: false,
          is_liked: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const updatedConversations = [newConversation, ...filteredConversations];
        return sortConversations(updatedConversations);
      });
    };
    
    // console.log('🎧 Setting up conversationCreated and conversationForked event listeners');
    window.addEventListener('conversationCreated', handleConversationCreated);
    window.addEventListener('conversationForked', handleConversationForked);
    return () => {
      // console.log('🧹 Cleaning up conversationCreated and conversationForked event listeners');
      window.removeEventListener('conversationCreated', handleConversationCreated);
      window.removeEventListener('conversationForked', handleConversationForked);
    };
  }, []); // Remove dependencies to prevent event listener cleanup/setup cycles

  const handleConversationClick = (conversation) => {
    // Extract conversation ID
    const conversationId = conversation.conversation_id || conversation.id;
  
    // Check if we're already on this conversation to avoid duplicate history entries
    const currentPath = window.location.pathname;
    const targetPath = `/c/${conversationId}`;
    
    // Only navigate if we're not already on this conversation
    if (currentPath !== targetPath) {
      // Use navigate for client-side routing - now uses nested route structure
      navigate(`/c/${conversationId}`);
    }
  
    // Set selected conversation ID immediately for UI feedback
    setSelectedConversationId(conversationId);
  
    // Create conversation data object
    const conversationData = {
      ...conversation,
      id: conversation.id || conversationId,
      conversation_id: conversation.conversation_id || conversationId,
    };
  
    // Call onSelectConversation if provided
    if (onSelectConversation) {
      onSelectConversation(conversationData);
    }
  
    // Collapse sidebar on mobile
    if (isMobile && isSidebarOpen) {
      handleToggleSidebar();
    }
  };

  // Handler functions for menu options
  const handlePinConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering conversation click
    setOpenMenuId(null);

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!authToken || !userId) return;

      // Get the correct conversation ID format
      const conversation = conversations?.find(
        convo => convo && (convo.id === conversationId || convo.conversation_id === conversationId)
      );

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Check current pin status
      const isPinned = conversation.is_pinned;
      // console.log(`${isPinned ? 'Unpinning' : 'Pinning'} conversation:`, conversationId);

      // Ensure we have the UUID from the conversation data
      const conversationUUID = conversation.conversation_id || conversation.id;
      // console.log('Using conversation UUID:', conversationUUID);

      // Use the pin API for both pin and unpin operations
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.PIN}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          chat_id: conversationUUID,
          pin: !isPinned // Set to true to pin, false to unpin
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isPinned ? 'unpin' : 'pin'} conversation: ${response.status}`);
      }

      // Update conversation in local state
      const currentTimestamp = new Date().toISOString();

      setConversations(prevConversations => {
        // First, update the pin status of the target conversation
        const updatedConversations = prevConversations.map(convo =>
          (convo.id === conversationId || convo.conversation_id === conversationId)
            ? {
              ...convo,
              is_pinned: !isPinned,
              pin_timestamp: !isPinned ? currentTimestamp : null
            }
            : convo
        );

        // Use the helper function to sort conversations
        return sortConversations(updatedConversations);
      });

    } catch (error) {
      console.error('Error handling pin/unpin operation:', error);
    }
  };

  const handleArchiveConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering conversation click
    // console.log('Archiving conversation:', conversationId);
    setOpenMenuId(null);

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!authToken || !userId) return;

      // Get the correct conversation ID format
      const conversation = conversations?.find(
        convo => convo && (convo.id === conversationId || convo.conversation_id === conversationId)
      );

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Ensure we have the UUID from the conversation data
      const conversationUUID = conversation.conversation_id || conversation.id;
      // console.log('Using conversation UUID for archiving:', conversationUUID);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.ARCHIVE}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId,
          chat_id: conversationUUID
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to archive conversation: ${response.status}`);
      }

      // Update conversation in local state
      setConversations(prevConversations => {
        // Mark the conversation as archived instead of completely removing it
        const updatedConversations = prevConversations.map(convo =>
          (convo.id === conversationId || convo.conversation_id === conversationId)
            ? { ...convo, is_archived: true }
            : convo
        );

        // Use the helper function to sort conversations
        return sortConversations(updatedConversations);
      });

    } catch (error) {
      console.error('Error archiving conversation:', error);
    }
  };

  const handleDeleteConversation = async (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering conversation click
    // console.log('Deleting conversation:', conversationId);
    setOpenMenuId(null);

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      if (!authToken || !userId) return;

      // Get the correct conversation ID format from the chat_history
      const conversation = conversations?.find(
        convo => convo && (convo.id === conversationId || convo.conversation_id === conversationId)
      );

      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Ensure we have the UUID from the conversation data
      const conversationUUID = conversation.conversation_id || conversation.id;
      // console.log('Using conversation UUID for deletion:', conversationUUID);

      // Check if the user is currently viewing this conversation
      const currentPath = window.location.pathname;
      const isCurrentlyViewing = currentPath === `/c/${conversationId}` || currentPath === `/c/${conversationUUID}`;

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.DELETE}/${conversationUUID}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to delete conversation: ${response.status}`);
      }

      // Remove conversation from local state
      setConversations(prevConversations =>
        prevConversations.filter(convo =>
          convo.id !== conversationId && convo.conversation_id !== conversationId
        )
      );

      // If user was viewing the deleted conversation, redirect to frontend URL using navigate
      if (isCurrentlyViewing) {
        navigate('/');
      }

    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const handleRenameConversation = async (conversationId, event) => {
    event.stopPropagation();
    event.preventDefault();
    setOpenMenuId(null);




    // Find the conversation to get its current title
    const conversation = conversations.find(convo => 
      convo.id === conversationId || convo.conversation_id === conversationId
    );

    if (!conversation) {
      console.error('Conversation not found');
      alert('Conversation not found. Please refresh the page and try again.');
      return;
    }



    // Get the full title (not truncated)
    const currentTitle = conversation.conversations?.title ||
      conversation.title ||
      (conversation.chat_history && conversation.chat_history[0]?.content) ||
      'New Conversation';

    // Start editing mode
    setEditingConversationId(conversationId);
    setEditTitle(currentTitle);
  };

  const handleSaveRename = async () => {
    if (!editTitle.trim()) {
      alert('Title cannot be empty');
      return;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');

      if (!authToken || !userId) {
        console.error('Missing authentication credentials');
        return;
      }

      // Find the conversation to get the correct ID
      const conversation = conversations.find(convo => 
        convo.id === editingConversationId || convo.conversation_id === editingConversationId
      );

      if (!conversation) {
        console.error('Conversation not found for editing');
        alert('Conversation not found. Please refresh the page and try again.');
        return;
      }

      // Use the correct conversation ID (prefer conversation_id over id)
      const correctConversationId = conversation.conversation_id || conversation.id;

  


      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.CONVERSATIONS.UPDATE_TITLE}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          conversation_id: correctConversationId,
          title: editTitle.trim(),
          user_id: userId
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail?.[0]?.msg || `Failed to update conversation title: ${response.status}`);
      }

      // Update local state
      setConversations(prevConversations =>
        prevConversations.map(convo => {
          if (convo.id === editingConversationId || convo.conversation_id === editingConversationId) {
            return {
              ...convo,
              title: editTitle.trim(),
              conversations: {
                ...convo.conversations,
                title: editTitle.trim()
              }
            };
          }
          return convo;
        })
      );

      // Exit editing mode
      setEditingConversationId(null);
      setEditTitle('');

    } catch (error) {
      console.error('Error updating conversation title:', error);
      alert('Failed to update conversation title. Please try again.');
    }
  };

  const handleCancelRename = () => {
    setEditingConversationId(null);
    setEditTitle('');
  };

  const toggleMenu = (conversationId, event) => {
    event.stopPropagation(); // Prevent triggering conversation click
    event.preventDefault();
    if (openMenuId === conversationId) {
      setOpenMenuId(null);
      setOpenUpwards(false);
    } else {
      // Measure position to decide direction
      const menuBtn = menuRefs.current[conversationId];
      const sidebar = sidebarRef.current;
      if (menuBtn && sidebar) {
        const btnRect = menuBtn.getBoundingClientRect();
        const sidebarRect = sidebar.getBoundingClientRect();
        // Estimate menu height (e.g., 150px)
        const menuHeight = 150;
        // If not enough space below, open upwards
        if (btnRect.bottom + menuHeight > sidebarRect.bottom - 60) { // 60px buffer for footer
          setOpenUpwards(true);
        } else {
          setOpenUpwards(false);
        }
      } else {
        setOpenUpwards(false);
      }
      setOpenMenuId(conversationId);
    }
  };

  const handleEditTitle = (conversationId, currentTitle) => {
    setEditingTitleId(conversationId);
    setEditingTitleValue(currentTitle);
  };
  const handleEditTitleChange = (e) => setEditingTitleValue(e.target.value);
  const handleEditTitleBlur = async (conversationId, userId) => {

    
    // Find the conversation object to see its structure
    const conversation = conversations.find(c => (c.id === conversationId || c.conversation_id === conversationId));

    
    // Use the correct conversation ID - prioritize conversation_id over id
    const correctConversationId = conversation?.conversation_id || conversation?.id || conversationId;

    
    // Get user ID from localStorage as fallback
    const actualUserId = userId || localStorage.getItem('userId');
    
    if (editingTitleValue.trim() && editingTitleId === conversationId) {
      try {
        
        
        if (!actualUserId) {
          throw new Error('No user ID available');
        }
        
        const apiResponse = await updateConversationTitle(correctConversationId, editingTitleValue.trim(), actualUserId);
    
        
        // Update the conversations state with the new title
        setConversations(prev => {
          
          
          const updated = prev.map(c => {
            const cId = c.id || c.conversation_id;
        
            
            if (cId === conversationId) {
              return { ...c, title: editingTitleValue.trim() };
            }
            return c;
          });
          
      
          
          // Verify the update was applied
          const updatedConversation = updated.find(c => (c.id === conversationId || c.conversation_id === conversationId));
          if (updatedConversation) {
        
          } else {
        
          }
          
          return updated;
        });
        
        // Also update the state immediately with a callback to ensure it's applied
        setTimeout(() => {
          setConversations(currentState => {
        
            const immediateUpdated = currentState.map(c => {
              const cId = c.id || c.conversation_id;
              if (cId === conversationId) {
                console.log('IMMEDIATE UPDATE: Setting title to:', editingTitleValue.trim());
                return { 
                  ...c, 
                  title: editingTitleValue.trim(),
                  ...(c.conversations && { conversations: { ...c.conversations, title: editingTitleValue.trim() } })
                };
              }
              return c;
            });
            // console.log('Immediate updated state:', immediateUpdated);
            return immediateUpdated;
          });
        }, 0);
        
        // Use requestAnimationFrame for the most immediate update
        requestAnimationFrame(() => {
          setConversations(currentState => {
            // console.log('=== RAF STATE UPDATE ===');
            const rafUpdated = currentState.map(c => {
              const cId = c.id || c.conversation_id;
              if (cId === conversationId) {
                // console.log('RAF UPDATE: Setting title to:', editingTitleValue.trim());
                return { 
                  ...c, 
                  title: editingTitleValue.trim(),
                  ...(c.conversations && { conversations: { ...c.conversations, title: editingTitleValue.trim() } })
                };
              }
              return c;
            });
            // console.log('RAF updated state:', rafUpdated);
            return rafUpdated;
          });
        });
        
        // Set flag to indicate we have local updates
        setHasLocalUpdates(true);
        
        // Track which conversation was updated
        setUpdatedConversationId(conversationId);
        
        // Store the updated title in a separate state
        setUpdatedTitles(prev => ({
          ...prev,
          [conversationId]: editingTitleValue.trim()
        }));
        
        // Force a complete re-render by updating the key
        setForceRenderKey(prev => prev + 1);
        
        // Force multiple re-renders to ensure the UI updates
        setTimeout(() => {
          // console.log('=== FORCING RE-RENDER 1 ===');
          setConversations(currentConversations => {
            // console.log('Re-render 1 - Current conversations:', currentConversations);
            return [...currentConversations]; // Force a new array reference
          });
        }, 50);
        
        setTimeout(() => {
          // console.log('=== FORCING RE-RENDER 2 ===');
          setConversations(currentConversations => {
            // console.log('Re-render 2 - Current conversations:', currentConversations);
            return [...currentConversations]; // Force another new array reference
          });
        }, 150);
        
        setTimeout(() => {
          // console.log('=== FORCING RE-RENDER 3 ===');
          setConversations(currentConversations => {
          //  console.log('Re-render 3 - Current conversations:', currentConversations);
            return [...currentConversations]; // Force another new array reference
          });
          
          // Clear the local updates flag after a delay to allow future external updates
          setTimeout(() => {
            setHasLocalUpdates(false);
            setUpdatedConversationId(null);
            setUpdatedTitles({});
            // console.log('Cleared hasLocalUpdates flag and updated titles');
          }, 2000);
        }, 250);
        
        // console.log('Title updated successfully');
        
        // Don't call fetchHistory here as it overrides local state changes
        // The local state update above should be sufficient
        
      } catch (error) {
        console.error('Error updating conversation title:', error);
        
        // Debug: Check what conversations exist for this user
        try {
          const debugData = await debugConversations(actualUserId);
          // console.log('=== DEBUG DATA ===');
          // console.log('Debug response:', debugData);
        } catch (debugError) {
          console.error('Error calling debug endpoint:', debugError);
        }
        
        // Optionally show error to user
      }
    }
    setEditingTitleId(null);
    setEditingTitleValue('');
  };
  const handleEditTitleKeyDown = (e, conversationId, userId) => {
    if (e.key === 'Enter') {
      handleEditTitleBlur(conversationId, userId);
    } else if (e.key === 'Escape') {
      setEditingTitleId(null);
      setEditingTitleValue('');
    }
  };

  // Handle sharing conversation
  const handleShareConversation = async (conversationId, event) => {
    event.stopPropagation();
    setOpenMenuId(null);
    
    try {
      const shareUrl = `${API_CONFIG.FRONTEND_URL}/share/${conversationId}`;
      if (navigator.clipboard && window.isSecureContext) {
        
      await navigator.clipboard.writeText(shareUrl);}
      else {
        // Fallback for insecure context or unsupported browsers
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        textArea.style.position = "fixed"; // avoid scrolling to bottom
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
  
      
      // Show success notification
      const notification = document.createElement('div');
      notification.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #28a745;
        color: white;
        padding: 12px 20px;
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        font-size: 14px;
        max-width: 300px;
        word-wrap: break-word;
      `;
      notification.textContent = 'Share link copied to clipboard!';
      document.body.appendChild(notification);
      
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 3000);
    } catch (error) {
      console.error('Failed to copy share link:', error);
      
      // Fallback: show the URL in an alert
      const shareUrl = `${window.location.origin}/share/${conversationId}`;
      alert(`Share this link: ${shareUrl}`);
    }
  };

  const SidebarHeader = () => (
    <div className="sidebar-header">
      <Link to="/">
        <img src={Geohublogoo} alt="GeoHub Logo" className="logo" style={{ height: '48px', width: '90%', objectFit: 'contain' }} />
      </Link>

      {isAuthenticated && (
        <button 
          className="new-chat-btn" 
          onClick={(e) => {
            e.preventDefault();
            // Clear assistantInfo from localStorage when starting a new chat
            localStorage.removeItem('assistantInfo');
            // console.log('Cleared assistantInfo from localStorage for new chat');
            
            // Clear downloadedFileIds from localStorage when starting a new chat
            localStorage.removeItem('downloadedFileIds');
            // console.log('Cleared downloadedFileIds from localStorage for new chat');
            
            navigate('/'); // Navigate to root using React Router
            setSelectedConversationId(null); // Clear selection
            onNewChat();
            // Collapse sidebar on mobile after starting new chat
            if (isMobile && isSidebarOpen) {
              handleToggleSidebar();
            }
          }} 
          title="Start New Chat"
        >
          <svg width="24" height="24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M15.6729 3.91287C16.8918 2.69392 18.8682 2.69392 20.0871 3.91287C21.3061 5.13182 21.3061 7.10813 20.0871 8.32708L14.1499 14.2643C13.3849 15.0293 12.3925 15.5255 11.3215 15.6785L9.14142 15.9899C8.82983 16.0344 8.51546 15.9297 8.29289 15.7071C8.07033 15.4845 7.96554 15.1701 8.01005 14.8586L8.32149 12.6785C8.47449 11.6075 8.97072 10.615 9.7357 9.85006L15.6729 3.91287ZM18.6729 5.32708C18.235 4.88918 17.525 4.88918 17.0871 5.32708L11.1499 11.2643C10.6909 11.7233 10.3932 12.3187 10.3014 12.9613L10.1785 13.8215L11.0386 13.6986C11.6812 13.6068 12.2767 13.3091 12.7357 12.8501L18.6729 6.91287C19.1108 6.47497 19.1108 5.76499 18.6729 5.32708ZM11 3.99929C11.0004 4.55157 10.5531 4.99963 10.0008 5.00007C9.00227 5.00084 8.29769 5.00827 7.74651 5.06064C7.20685 5.11191 6.88488 5.20117 6.63803 5.32695C6.07354 5.61457 5.6146 6.07351 5.32698 6.63799C5.19279 6.90135 5.10062 7.24904 5.05118 7.8542C5.00078 8.47105 5 9.26336 5 10.4V13.6C5 14.7366 5.00078 15.5289 5.05118 16.1457C5.10062 16.7509 5.19279 17.0986 5.32698 17.3619C5.6146 17.9264 6.07354 18.3854 6.63803 18.673C6.90138 18.8072 7.24907 18.8993 7.85424 18.9488C8.47108 18.9992 9.26339 19 10.4 19H13.6C14.7366 19 15.5289 18.9992 16.1458 18.9488C16.7509 18.8993 17.0986 18.8072 17.362 18.673C17.9265 18.3854 18.3854 17.9264 18.673 17.3619C18.7988 17.1151 18.8881 16.7931 18.9393 16.2535C18.9917 15.7023 18.9991 14.9977 18.9999 13.9992C19.0003 13.4469 19.4484 12.9995 20.0007 13C20.553 13.0004 21.0003 13.4485 20.9999 14.0007C20.9991 14.9789 20.9932 15.7808 20.9304 16.4426C20.8664 17.116 20.7385 17.7136 20.455 18.2699C19.9757 19.2107 19.2108 19.9756 18.27 20.455C17.6777 20.7568 17.0375 20.8826 16.3086 20.9421C15.6008 21 14.7266 21 13.6428 21H10.3572C9.27339 21 8.39925 21 7.69138 20.9421C6.96253 20.8826 6.32234 20.7568 5.73005 20.455C4.78924 19.9756 4.02433 19.2107 3.54497 18.2699C3.24318 17.6776 3.11737 17.0374 3.05782 16.3086C2.99998 15.6007 2.99999 14.7266 3 13.6428V10.3572C2.99999 9.27337 2.99998 8.39922 3.05782 7.69134C3.11737 6.96249 3.24318 6.3223 3.54497 5.73001C4.02433 4.7892 4.78924 4.0243 5.73005 3.54493C6.28633 3.26149 6.88399 3.13358 7.55735 3.06961C8.21919 3.00673 9.02103 3.00083 9.99922 3.00007C10.5515 2.99964 10.9996 3.447 11 3.99929Z"
              fill="currentColor"
            />
          </svg>
        </button>
      )}
      
      <button className="sidebar-toggle-btn" onClick={handleToggleSidebar}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );

  // Return early with null if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`} 
        onClick={handleToggleSidebar}
      />
      
      {/* Mobile toggle button - only visible when sidebar is closed and user is authenticated */}
      {isAuthenticated && !isSidebarOpen && (
        <div className="sidebar-toggle-container">
          <button
            className="sidebar-toggle mobile-only"
            onClick={handleToggleSidebar}
            style={{ position: "absolute", top: "-17px", left: "-11px", zIndex: 1 }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
          {/* <img src={Geohublogo} alt="GeoHub Logo" className="mobile-logo" /> */}
        </div>
      )}
    
     

      <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`} ref={sidebarRef}>
        {/* HEADER - Logo + New Chat Icon */}
        <SidebarHeader />

        {/* SEARCH BAR */}
        <div className="search-bar-container">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="search-icon"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>
          <div className="search-input-wrapper">
            <input
              type="text"
              placeholder="Search Chat..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {(searchQuery || dateFrom || dateTo) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setDateFrom('');
                  setDateTo('');
                  setSelectedQuickFilter('');
                }}
                className="clear-search-btn"
                title="Clear all filters"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <button
            onClick={() => setShowDateFilters(!showDateFilters)}
            className={`date-filter-toggle ${showDateFilters ? 'active' : ''}`}
            title="Toggle date filters"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
          </button>
        </div>

        {/* DATE FILTERS */}
        {showDateFilters && (
          <div className="date-filters-container">
            {/* Quick Filters */}
            <div className="quick-filters-section">
              <h4 className="quick-filters-title">Quick Filters</h4>
              <div className="quick-filters-grid">
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'today' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('today')}
                >
                  Today
                </button>
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'yesterday' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('yesterday')}
                >
                  Yesterday
                </button>
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'past7days' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('past7days')}
                >
                  Past 7 days
                </button>
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'past30days' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('past30days')}
                >
                  Past 30 days
                </button>
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'thismonth' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('thismonth')}
                >
                  This month
                </button>
                <button
                  className={`quick-filter-btn ${selectedQuickFilter === 'clear' ? 'active' : ''}`}
                  onClick={() => handleQuickFilter('clear')}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CHAT HISTORY */}
        <div className="chat-history-container">
          {conversations && conversations.length > 0 ? (
            (() => {
              const filteredResults = filterConversations(conversations, searchQuery, dateFrom, dateTo)
                .filter(convo => convo && !convo.is_archived);
              
              if ((searchQuery || dateFrom || dateTo) && filteredResults.length === 0) {
                return (
                  <div className="no-search-results">
                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <p>No conversations found for your filters</p>
                    <p className="search-hint">
                      {searchQuery && `Search: "${searchQuery}"`}
                      {dateFrom && dateTo && ` Date range: ${new Date(dateFrom).toLocaleDateString()} - ${new Date(dateTo).toLocaleDateString()}`}
                      {dateFrom && !dateTo && ` From: ${new Date(dateFrom).toLocaleDateString()}`}
                      {!dateFrom && dateTo && ` To: ${new Date(dateTo).toLocaleDateString()}`}
                    </p>
                    <p className="search-hint">Try different keywords or adjust your date range</p>
                  </div>
                );
              }
              
              return (
                <ul className="chat-history">
                  {filteredResults.map((convo) => {
                    // Get the full title before truncation
                    const fullTitle = convo.conversations?.title ||
                      convo.title ||
                      (convo.chat_history && convo.chat_history[0]?.content) ||
                      'New Conversation';

                    // Truncated title for display
                    const displayTitle = fullTitle.length > 30 ? fullTitle.substring(0, 30) + '...' : fullTitle;

                    // Get the conversation ID (handle different structures)
                    const conversationId = convo.id || convo.conversation_id;
                    const conversationId1 = convo.conversation_id ||convo.id; 

                    return (
                      <li
                        key={conversationId}
                        className={`chat-item ${isConversationSelected(convo) ? 'selected' : ''}`}
                        onClick={() => {
                          if (!isConversationSelected(convo)) {
                            handleConversationClick(convo);
                          }
                        }}
                        title={fullTitle}
                        data-pinned={convo.is_pinned ? "true" : "false"}
                      >
                        <div className="chat-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                          </svg>
                        </div>
                        <div className="chat-info">
                          <div className="chat-title">
    {editingTitleId === conversationId ? (
    <input
      value={editingTitleValue}
      onChange={handleEditTitleChange}
      onBlur={() => handleEditTitleBlur(conversationId, localStorage.getItem('userId'))}
      onKeyDown={e => handleEditTitleKeyDown(e, conversationId, localStorage.getItem('userId'))}
      autoFocus
      style={{ width: '90%', fontSize: '0.9rem', fontWeight: 600, border: '1px solid #ccc', borderRadius: 4, padding: '2px 6px' }}
    />
  ) : (
    <ReactMarkdown
        children={displayTitle}
        disallowedElements={['img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6']}
        unwrapDisallowed={true}
      />
        )}
</div>
                          <div className="chat-date">
                            {new Date(convo.updated_at).toLocaleDateString()}
                            {convo.is_pinned && (
                              <span className="pin-indicator" title="Pinned conversation">
                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none" style={{ marginLeft: '6px', color: '#2241DD' }}>
                                  <path d="M16 4v4h.01L16 8a2 2 0 0 1 1 1.73l-.001.127L17 10c0 .17-.05.33-.14.46l-.1.14L15 12.5v3.5h3v2h-5v4.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V18H7v-2h3v-3.5L8.24 10.6c-.09-.13-.14-.29-.14-.46l.001-.127A2 2 0 0 1 9 8.27L8.99 8H9V4a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1z" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Three-dot menu button */}
                        <div
                          className="menu-container"
                          ref={el => menuRefs.current[conversationId] = el}
                          style={{ position: 'relative', marginLeft: 'auto', zIndex: 999 }}
                        >
                          <button
                            className="menu-button"
                            onClick={(e) => toggleMenu(conversationId, e)}
                            aria-label="Conversation options"
                            aria-haspopup="true"
                            aria-expanded={openMenuId === conversationId}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '5px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#666',
                              opacity: '0.7',
                              borderRadius: '4px'
                            }}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="1"></circle>
                              <circle cx="12" cy="5" r="1"></circle>
                              <circle cx="12" cy="19" r="1"></circle>
                            </svg>
                          </button>

                          {openMenuId === conversationId && (
                            <div
                              role="menu"
                              style={{
                                position: 'absolute',
                                right: '0',
                                ...(openUpwards
                                  ? { bottom: '100%', top: 'auto', animation: 'menuFadeInBottom 0.2s ease' }
                                  : { top: '100%', bottom: 'auto', animation: 'menuFadeIn 0.2s ease' }
                                ),
                                background: 'white',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                width: '150px',
                                zIndex: '9999',
                                overflow: 'hidden'
                              }}
                            >
                              <button
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  width: '100%',
                                  padding: '10px 15px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#333',
                                  fontSize: '0.9rem'
                                }}
                                onClick={(e) => handlePinConversation(conversationId, e)}
                                role="menuitem"
                                title={convo.is_pinned ? "Unpin from top" : "Pin to top of the list"}
                              >
                                {convo.is_pinned ? (
                                  <>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                      <path d="M16 4v4h.01L16 8a2 2 0 0 1 1 1.73l-.001.127L17 10c0 .17-.05.33-.14.46l-.1.14L15 12.5v3.5h3v2h-5v4.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V18H7v-2h3v-3.5L8.24 10.6c-.09-.13-.14-.29-.14-.46l.001-.127A2 2 0 0 1 9 8.27L8.99 8H9V4a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1z" />
                                      <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth="2" />
                                    </svg>
                                    Unpin
                                  </>
                                ) : (
                                  <>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                                      <path d="M16 4v4h.01L16 8a2 2 0 0 1 1 1.73l-.001.127L17 10c0 .17-.05.33-.14.46l-.1.14L15 12.5v3.5h3v2h-5v4.5c0 .28-.22.5-.5.5s-.5-.22-.5-.5V18H7v-2h3v-3.5L8.24 10.6c-.09-.13-.14-.29-.14-.46l.001-.127A2 2 0 0 1 9 8.27L8.99 8H9V4a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1z" />
                                    </svg>
                                    Pin
                                  </>
                                )}
                              </button>
                              <button
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                width: '100%',
                                padding: '10px 15px',
                                border: 'none',
                                background: 'none',
                                textAlign: 'left',
                                cursor: 'pointer',
                                color: '#333',
                                fontSize: '0.9rem'
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                setEditingTitleId(conversationId);
                                setEditingTitleValue(fullTitle);
                                setOpenMenuId(null);
                              }}
                              role="menuitem"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                              Rename
                            </button>
                            <button
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  width: '100%',
                                  padding: '10px 15px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#333',
                                  fontSize: '0.9rem'
                                }}
                                onClick={(e) => handleShareConversation(conversationId1, e)}
                                role="menuitem"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="18" cy="5" r="3"></circle>
                                  <circle cx="6" cy="12" r="3"></circle>
                                  <circle cx="18" cy="19" r="3"></circle>
                                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
                                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
                                </svg>
                                Share
                              </button>
                            <button
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  width: '100%',
                                  padding: '10px 15px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#333',
                                  fontSize: '0.9rem'
                                }}
                                onClick={(e) => handleArchiveConversation(conversationId, e)}
                                role="menuitem"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="5" rx="2"></rect>
                                  <path d="M5 9v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9"></path>
                                  <path d="M10 13h4"></path>
                                </svg>
                                Archive
                              </button>
                              <button
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  width: '100%',
                                  padding: '10px 15px',
                                  border: 'none',
                                  background: 'none',
                                  textAlign: 'left',
                                  cursor: 'pointer',
                                  color: '#e53e3e',
                                  fontSize: '0.9rem'
                                }}
                                onClick={(e) => handleDeleteConversation(conversationId, e)}
                                role="menuitem"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: '#e53e3e' }}>
                                  <path d="M3 6h18"></path>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                </svg>
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              );
            })()
          ) : (
            <div className="no-chats">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
              <p>No conversations yet.</p>
              <p className="start-hint">Start a new chat to begin!</p>
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="sidebar-footer">
          <div className="profile-section">
            <div className="profile-container">
              <button
                className="profile-icon"
                onClick={handleOpenUserSettings}
                aria-label="Open user settings"
              >
                <div className="profile-img-wrapper">
                  <div
                    className="profile-img"
                    style={{
                      backgroundColor: "#2241DD",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.2rem",
                      fontWeight: "bold"
                    }}
                  >
                    {username ? username.charAt(0).toUpperCase() : "U"}
                  </div>
                  {/* <div className="status-indicator" /> */}
                </div>
              </button>
              <span className="user-name">
              <span className="name-text">
  {username ? username.charAt(0).toUpperCase() + username.slice(1) : ''}
</span>
              </span>
            </div>
          </div>
        </div>
      </aside>

      {/* STYLES */}
      <style jsx>{`
        .sidebar {
              width: 260px !important;
          height: 100vh;
          display: flex;
          flex-direction: column;
          background: white;
          color: black;
          box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
          z-index: 100;
              flex-shrink: 0;
        }

        .sidebar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          text-align: left;
          padding: 1.5rem 1rem 1rem;
          height: 100px;
        }

        .logo {
          width: 100%;
          max-width: 200px;
          height: auto;
          margin-top: -2rem;
          transition: all 0.3s ease;
        }

        .new-chat-btn {
          background: none;
          border: none;
          cursor: pointer;
          transition: transform 0.2s ease;
          margin-top: -1rem;
          padding: 8px;
          border-radius: 50%;
        }

        .new-chat-btn:hover {
          transform: scale(1.1);
          background-color: rgba(0, 0, 0, 0.05);
        }

        .search-bar-container {
          padding: 0.5rem 1rem;
          border-bottom: 1px solid #eee;
          background: #f5f7fa;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          border-radius: 8px;
          margin: 0 0.5rem 0.5rem 0.5rem;
          box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
          min-height: 40px;
        }

        .search-icon {
          color: #999;
          flex-shrink: 0;
          width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .search-input {
          flex: 1;
          border: none;
          outline: none;
          padding: 0.5rem 2.5rem 0.5rem 0.75rem;
          font-size: 0.9rem;
          background: transparent;
          color: #333;
          height: 32px;
          display: flex;
          align-items: center;
          width: 100%;
        }

        .search-input-wrapper {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
        }

        .clear-search-btn {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          padding: 2px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          transition: all 0.2s ease;
          width: 20px;
          height: 20px;
          z-index: 2;
        }

        .clear-search-btn:hover {
          background-color: rgba(0, 0, 0, 0.1);
          color: #666;
        }

        .search-input::placeholder {
          color: #999;
        }

        .no-search-results {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          text-align: center;
          color: #777;
          font-size: 0.9rem;
          height: 200px;
          background: #f9f9f9;
          border-radius: 10px;
          margin: 10px 0;
          width: 100%;
          max-width: 240px;
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        .no-search-results svg {
          margin-bottom: 15px;
          color: #999;
        }

        .search-hint {
          font-size: 0.8rem;
          color: #999;
          margin-top: 5px;
        }

        .chat-history-container {
          flex: 1;
          display: flex;
              // flex-direction: column;
          margin-top: -2px;
          padding: 0 5px;
          overflow: hidden;
              width: 100%;
        }

        .history-title {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #666;
          margin: 10px 0;
          padding: 0 8px;
        }

        .chat-history {
          list-style: none;
          padding: 0;
          margin: 0;
          max-height: calc(90vh - 120px);
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: #ddd transparent;
          text-align: left;
              width: 100%;
        }

        .chat-history::-webkit-scrollbar {
          width: 4px;
        }

        .chat-history::-webkit-scrollbar-track {
          background: transparent;
        }

        .chat-history::-webkit-scrollbar-thumb {
          background-color: #ddd;
          border-radius: 4px;
        }

        .chat-history::-webkit-scrollbar-thumb:hover {
          background-color: #bbb;
        }

        .chat-item {
          display: flex;
          padding: 12px 8px;
          border-radius: 8px;
          cursor: pointer;
              transition: background-color 0.2s ease;
          margin-bottom: 6px;
          border: 1px solid transparent;
          width: 100%;
          box-sizing: border-box;
              position: relative;
              flex-shrink: 0;
        }

        .chat-item:hover {
          background: #f5f7fa;
        }

        .chat-item.selected {
              background-color: #f0f4ff !important;
              border-left: 3px solid #2241DD;
              border-radius: 8px 8px 8px 8px;
        }

        .chat-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          min-width: 28px;
          background-color: #eef2ff;
          border-radius: 8px;
          margin-right: 10px;
          color: #2241dd;
        }

        .chat-info {
          flex: 1;
          min-width: 0;
          overflow: hidden;
        }

        .chat-title {
          font-weight: 600;
          margin-bottom: 4px;
          color: #333;
          font-size: 0.9rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          transition: color 0.2s ease;
        }

        .chat-item:hover .chat-title {
          color: #2241dd;
        }

        .chat-item.selected .chat-title {
          color: #2241dd;
        }

        .chat-date {
          font-size: 0.7rem;
          color: #999;
        }

        .no-chats,
        .not-authenticated {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 30px 20px;
          text-align: center;
          color: #777;
          font-size: 0.9rem;
          height: 200px;
          background: #f9f9f9;
          border-radius: 10px;
          margin: 10px 0;
          width: 100%;
          max-width: 240px; /* Slightly wider for better look */
          margin-left: auto;
          margin-right: auto;
          box-sizing: border-box;
        }

        .no-chats svg,
        .not-authenticated svg {
          margin-bottom: 15px;
          color: #999;
        }

        .start-hint {
          font-size: 0.8rem;
          color: #999;
          margin-top: 5px;
        }

        .sidebar-footer {
          padding: 1rem;
          background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          border-top: 1px solid #e0e0e0;
          position: relative;
          width: 100%;
          box-sizing: border-box;
          margin-top: auto;
          z-index: 10;
          display: flex;
          flex-direction: column;
        }

        /* Media queries for sidebar-footer */
        @media screen and (max-width: 768px) {
          .sidebar-footer {
            padding: 0.75rem;
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          }
        }

        @media screen and (max-width: 480px) {
          .sidebar-footer {
            padding: 0.5rem;
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          }
        }

        /* Specific Android device fixes */
        @media screen and (max-width: 360px) {
          .sidebar-footer {
            padding: 0.4rem;
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          }
        }

        @media screen and (max-height: 600px) {
          .sidebar-footer {
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          }
        }

        /* Fix for very small Android devices */
        @media screen and (max-width: 320px) {
          .sidebar-footer {
            padding: 0.35rem;
            position: sticky;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(135deg, #f5f7fa 0%, #e4e9f0 100%);
          }
        }

        .profile-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          width: 100%;
        }

        .profile-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.5rem;
          transition: background 0.2s ease;
          overflow: hidden;
        }

        .profile-container:hover {
          background: rgba(0, 0, 0, 0.03);
          border-radius: 8px;
        }

        .profile-icon {
          position: relative;
          border: none;
          background: none;
          padding: 0;
          cursor: pointer;
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }

        .profile-img-wrapper {
          position: relative;
          display: inline-flex;
          flex-shrink: 0;
        }

        .profile-img {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid #fff;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
          transition: all 0.2s ease;
        }

        .profile-icon:hover .profile-img {
          border-color: #007bff;
          transform: translateY(-2px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }

        .status-indicator {
          position: absolute;
          bottom: 2px;
          right: 2px;
          width: 10px;
          height: 10px;
          background: #00cc00;
          border-radius: 50%;
          border: 2px solid #fff;
          transition: transform 0.2s ease;
        }

        .profile-icon:hover .status-indicator {
          transform: scale(1.1);
        }

        .user-name {
          display: flex;
          flex-direction: column;
          line-height: 1.2;
          min-width: 0;
          flex: 1;
        }

        .name-text {
          font-size: 0.95rem;
          font-weight: 500;
          color: #2c3e50;
          transition: color 0.2s ease;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .online-status {
          font-size: 0.75rem;
          color: #00cc00;
          font-weight: 400;
          margin-top: 2px;
        }

        .login-prompt {
          font-size: 0.9rem;
          color: #666;
          font-style: italic;
        }

        .sidebar-toggle-btn {
          display: none;
          margin-top: -34px;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border: none;
          background: #f5f5f5;
          border-radius: 50%;
          cursor: pointer;
          color: #333;
        }

        .sidebar-toggle-fixed {
          position: fixed;
          left: 10px;
          top: 10px;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 99;
          cursor: pointer;
          border: none;
          transition: all 0.2s ease;
        }

        .sidebar-toggle-fixed:hover {
          transform: scale(1.05);
          background: #f5f7fa;
        }

        .sidebar-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* background: rgba(0, 0, 0, 0.3); */
          z-index: 50;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
        }

        .sidebar-overlay.visible {
          opacity: 1;
          
        }

        @media only screen and (max-width: 600px) {
          .sidebar {
            position: fixed;
            left: 0;
            top: 0;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
          }

          .sidebar.open {
            transform: translateX(0);
          }

          .sidebar-toggle-btn {
            display: flex;
          }

          .logo {
                max-width: 155px;
            margin-top: -2rem;
            margin-left: 0;
          }

          .sidebar.open .logo {
            margin-left: 0;
          }
        }

            /* Custom tooltip styles */
            .chat-item:hover::before {
              content: attr(title);
              position: absolute;
              left: calc(100% + 10px);  /* Fixed distance from the chat item */
              top: 50%;
              transform: translateY(-50%);
              background: rgba(0, 0, 0, 0.8);
              color: white;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 0.85rem;
              white-space: nowrap;  /* Ensure no wrapping */
              overflow: visible;    /* Allow content to overflow */
              text-overflow: clip;  /* Don't add ellipsis */
              max-width: none;      /* Remove any max-width constraint */
              width: auto;          /* Allow natural width */
              z-index: 1000;
              opacity: 0;
              animation: fadeIn 0.2s ease forwards;
              pointer-events: none;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            }

            /* Adjust position if tooltip would go off-screen */
            @media (min-width: 769px) {
              .chat-item:hover::before {
                max-width: calc(100vw - 100%);  /* Prevent going off screen horizontally */
                text-align: left;
              }
            }

            @keyframes fadeIn {
              to {
                opacity: 1;
              }
            }

            /* Hide tooltip on mobile */
            @media (max-width: 768px) {
              .chat-item:hover::before {
                display: none;
              }

              .sidebar-footer {
                padding: 0.75rem;
              }

              .profile-img {
                width: 36px;
                height: 36px;
              }

              .status-indicator {
                width: 8px;
                height: 8px;
                bottom: 1px;
                right: 1px;
              }

              .name-text {
                font-size: 0.9rem;
              }

              .online-status {
                font-size: 0.7rem;
              }
            }

            @media (max-width: 480px) {
              .sidebar-footer {
                padding: 0.5rem;
              }

              .profile-img {
                width: 32px;
                height: 32px;
              }

              .status-indicator {
                width: 7px;
                height: 7px;
                bottom: 1px;
                right: 1px;
              }

              .name-text {
                font-size: 0.85rem;
              }

              .online-status {
                font-size: 0.65rem;
              }

              .profile-container {
                gap: 0.5rem;
                padding: 0.35rem;
              }
            }

            /* Menu container styles */
            .menu-container {
              position: relative;
              margin-left: auto;
              z-index: 10;
            }
            
            .menu-button {
              background: none;
              border: none;
              cursor: pointer;
              padding: 5px;
              display: flex;
              alignItems: 'center';
              justifyContent: 'center';
              color: #666;
              opacity: 0;
              border-radius: 4px;
              transition: all 0.2s ease;
            }
            
            .menu-button:hover,
            .menu-button:focus {
              opacity: 1;
              background-color: rgba(0, 0, 0, 0.05);
            }
            
            .chat-item:hover .menu-button {
              opacity: 0.7;
            }
            
            /* Dropdown menu styles */
            .dropdown-menu {
              position: absolute;
              right: 0;
              top: 100%;
              background: white;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
              width: 150px;
              z-index: 9999;
              overflow: hidden;
              animation: menuFadeIn 0.2s ease;
              max-width: calc(100% - 10px);
              pointer-events: auto;
            }
            
            /* Reposition the dropdown when close to the bottom of the sidebar */
            .chat-item:last-child .dropdown-menu,
            .chat-item:nth-last-child(1) .dropdown-menu,
            .chat-item:nth-last-child(2) .dropdown-menu {
              bottom: 100%;
              top: auto;
              animation: menuFadeInBottom 0.2s ease;
            }
            
            @keyframes menuFadeIn {
              from {
                opacity: 0;
                transform: translateY(-8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            @keyframes menuFadeInBottom {
              from {
                opacity: 0;
                transform: translateY(8px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            
            .menu-item {
              display: flex;
              alignItems: 'center';
              gap: 10px;
              width: 100%;
              padding: 10px 15px;
              border: none;
              background: none;
              textAlign: 'left';
              cursor: pointer;
              transition: background 0.2s ease;
              color: #333;
              fontSize: '0.9rem';
            }
            
            .menu-item:hover,
            .menu-item:focus {
              background-color: #f5f7fa;
            }
            
            .menu-item svg {
              color: #666;
            }
            
            .menu-item.delete-item {
              color: #e53e3e;
            }
            
            .menu-item.delete-item svg {
              color: #e53e3e;
            }
            
            .menu-item.delete-item:hover,
            .menu-item.delete-item:focus {
              background-color: #fff5f5;
            }

            /* Styles for pinned conversations */
            .pin-indicator {
              display: inline-flex;
              align-items: center;
              vertical-align: middle;
              margin-left: 6px;
            }

            /* Remove background styling for pinned conversations - just show pin icon */
            .chat-item[data-pinned="true"] .pin-indicator svg {
              filter: drop-shadow(0 0 1px rgba(34, 65, 221, 0.3));
            }

        .sidebar-toggle-container {
            position: fixed;
            top: 1rem;
            left: 1rem;
            z-index: 100;
            display: flex;
            align-items: center;
        }

        .sidebar-toggle-fixed {
            
            border: none;
            cursor: pointer;
            padding: 0;
            display: flex;
            align-items: center;
        }

        .mobile-logo {
            display: none;
            height: 30px;
            margin-left: 1rem;
        }

        @media (max-width: 768px) {
            .mobile-logo {
                display: block;
                width: 68%;
                margin-left: 3rem;
                height: 23px;
          }
        }

        .date-filter-toggle {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #999;
          transition: all 0.2s ease;
          flex-shrink: 0;
          width: 24px;
          height: 24px;
        }

        .date-filter-toggle:hover {
          background-color: rgba(0, 0, 0, 0.1);
          color: #666;
        }

        .date-filter-toggle.active {
          background-color: rgba(34, 65, 221, 0.1);
          color: #2241DD;
        }

        .date-filters-container {
          padding: 0.75rem 1rem;
          background: #f8f9fa;
          border-bottom: 1px solid #eee;
          margin: 0 0.5rem 0.5rem 0.5rem;
          border-radius: 8px;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .quick-filters-section {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .quick-filters-title {
          font-size: 0.85rem;
          font-weight: 600;
          color: #333;
          margin: 0;
        }

        .quick-filters-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 0.5rem;
        }

        .quick-filter-btn {
          padding: 0.5rem 0.75rem;
          border: 1px solid #ddd;
          border-radius: 6px;
          background: white;
          color: #666;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: center;
        }

        .quick-filter-btn:hover {
          background: #f0f4ff;
          border-color: #2241DD;
          color: #2241DD;
        }

        .quick-filter-btn.active {
          background: #2241DD;
          border-color: #2241DD;
          color: white;
        }

        @media (max-width: 768px) {
          .date-filters-container {
            flex-direction: column;
            gap: 0.75rem;
            padding: 1rem;
            align-items: stretch;
          }

          .quick-filters-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 0.4rem;
          }

          .quick-filter-btn {
            padding: 0.4rem 0.5rem;
            font-size: 0.75rem;
          }

          .search-bar-container {
            padding: 0.75rem;
            margin: 0 0.25rem 0.25rem 0.25rem;
          }
        }
      `}</style>
    </>
  );
};

Sidebar.propTypes = {
  onNewChat: PropTypes.func.isRequired,
  handleOpenUserSettings: PropTypes.func.isRequired,
  isSidebarOpen: PropTypes.bool.isRequired,
  handleToggleSidebar: PropTypes.func.isRequired,
  onSelectConversation: PropTypes.func,
  currentConversation: PropTypes.array,
  currentConversationId: PropTypes.string,
  externalConversations: PropTypes.array
};

export default Sidebar;